import React from "react";
import { parseISO } from "date-fns";
import { Countdown } from "@/features/shell/components/shared/Countdown";
import { ChevronRightIcon, PromoInsideIcon } from "@picklebet/ui";
import { Link } from "@/features/shell/components/shared/Link/Link";
import { isBettingEvent, useEvent } from "@/hooks/firestore/betting/useEvent";
import { EventStatus } from "@/features/shell/components/shared/EventStatus";
import { useDebounce } from "usehooks-ts";
import cx from "classnames";
import * as styles from "@/features/search/components/Results/Results.module.scss";

type RowsProps = {
  icon: React.ReactNode;
  title: React.ReactNode;
  subTitle?: React.ReactNode;
  highlight?: React.ReactNode;
  to?: string;
  startTime?: string;
  eventId?: string;
  iconClassName?: string;
};

export const Row = ({
  icon,
  title,
  subTitle,
  highlight,
  to,
  startTime,
  eventId,
  iconClassName,
}: RowsProps) => {
  // debouncing eventId here is important for input and results performance as it
  // helps reduce the number of requests being sent while the user is typing
  const debouncedEventId = useDebounce(eventId, 100);
  const { event } = useEvent(debouncedEventId, "all");

  const parsedStartTime = startTime ? parseISO(startTime) : null;
  const shouldShowButtons = Boolean(to) || Boolean(startTime);

  const getStatusComponent = () => {
    if (event) {
      const eventIsBetting = isBettingEvent(event);
      const scheduledStartTime =
        (eventIsBetting && event.nextBetStop) || event.scheduledStartTime;
      const bettingType = eventIsBetting
        ? event.mainMarket?.bettingType
        : undefined;

      return (
        <EventStatus
          status={event.status}
          scheduledStartTime={scheduledStartTime}
          bettingType={bettingType}
          clampClassName={styles.countdown}
        />
      );
    }

    return (
      parsedStartTime && (
        <Countdown
          clampClassName={styles.countdown}
          scheduledStartTime={parsedStartTime}
        />
      )
    );
  };

  const statusComponent = shouldShowButtons ? getStatusComponent() : null;

  const rowContents = (
    <>
      <div className={styles.iconWrapper}>
        <div className={cx({ [styles.icon]: !iconClassName }, iconClassName)}>
          {icon}
        </div>

        {event?.showPromoBanner && (
          <div className={styles.promoIcon}>
            <PromoInsideIcon />
          </div>
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.title}>{title}</div>
        {subTitle && <div className={styles.subTitle}>{subTitle}</div>}
        {highlight && <div>{highlight}</div>}
      </div>

      {shouldShowButtons && (
        <div className={styles.cluster}>
          {statusComponent}
          {to && (
            <div className={styles.link}>
              <ChevronRightIcon />
            </div>
          )}
        </div>
      )}
    </>
  );

  if (to) {
    return (
      <Link className={styles.row} to={to}>
        {rowContents}
      </Link>
    );
  }

  return <div className={styles.row}>{rowContents}</div>;
};
